import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { flagUrl } from "@/lib/countries";
import { fmtUAE, TZ_LABEL } from "@/lib/dates";
import { toast } from "sonner";
import { Pencil, Check, X } from "lucide-react";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [bonus, setBonus] = useState([]);
  const [matchesById, setMatchesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/predictions/mine"),
      api.get("/matches"),
      api.get("/bonus/predictions/mine"),
    ])
      .then(([p, m, b]) => {
        setPredictions(p.data);
        const map = {};
        m.data.forEach((mm) => { map[mm.id] = mm; });
        setMatchesById(map);
        setBonus(b.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const finished = predictions.filter((p) => matchesById[p.match_id]?.status === "finished");
  const matchPoints = finished.reduce((s, p) => s + (p.points || 0), 0);
  const bonusPoints = bonus.reduce((s, b) => s + (b.points || 0), 0);
  const totalPoints = matchPoints + bonusPoints;
  const exactCount = finished.filter((p) => (p.score_points ?? 0) > 0).length;
  const bttsCount = finished.filter((p) => p.btts_points === 1).length;

  return (
    <div className="min-h-screen">
      <Navbar />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-lavender mb-3">Your Locker</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
            {user?.name?.split(" ")[0] || "Player"}&apos;s <span className="text-purple">Card</span>
          </h1>
          {editingName ? (
            <div className="mt-4 flex items-center gap-2 max-w-md" data-testid="profile-name-editor">
              <input
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={60}
                autoFocus
                placeholder="Your real name"
                data-testid="profile-name-input"
                className="flex-1 bg-surface border border-purple/40 rounded-full px-4 py-2.5 text-white placeholder:text-white/30 focus:border-purple focus:ring-1 focus:ring-purple outline-none text-sm"
              />
              <button
                type="button"
                onClick={async () => {
                  const next = nameDraft.trim();
                  if (next.length < 2) { toast.error("Name too short"); return; }
                  setSavingName(true);
                  try {
                    const { data } = await api.patch("/me/name", { name: next });
                    updateUser(data);
                    toast.success("Name updated");
                    setEditingName(false);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Update failed");
                  } finally {
                    setSavingName(false);
                  }
                }}
                disabled={savingName}
                data-testid="profile-name-save"
                className="btn-bounce w-10 h-10 rounded-full bg-gradient-to-br from-vivid to-purple text-white grid place-items-center disabled:opacity-50"
                aria-label="Save name"
              >
                <Check size={16}/>
              </button>
              <button
                type="button"
                onClick={() => { setEditingName(false); setNameDraft(user?.name || ""); }}
                data-testid="profile-name-cancel"
                className="w-10 h-10 rounded-full bg-surface border border-white/10 text-white/70 grid place-items-center hover:text-lavender hover:border-lavender transition-colors"
                aria-label="Cancel"
              >
                <X size={16}/>
              </button>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <p className="text-white/60 text-sm">
                <span className="font-semibold">{user?.name}</span> · @<span className="text-lavender">{user?.league_name}</span>
              </p>
              <button
                type="button"
                onClick={() => { setNameDraft(user?.name || ""); setEditingName(true); }}
                data-testid="profile-name-edit-btn"
                className="btn-bounce inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase font-bold text-white bg-gradient-to-r from-vivid to-purple border border-purple rounded-full px-3 py-1.5 shadow-[0_4px_14px_rgba(160,64,192,0.45)] hover:from-purple hover:to-lavender hover:text-plum transition-all"
              >
                <Pencil size={12}/> Edit name
              </button>
            </div>
          )}
          <p className="text-white/40 text-[11px] mt-2">Your league name (@{user?.league_name}) is fixed — it&apos;s how you log in.</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat label="Total Points" value={totalPoints} accent />
          <Stat label="Match Points" value={matchPoints} />
          <Stat label="Bonus Points" value={bonusPoints} />
          <Stat label="Exact Scores" value={exactCount} />
          <Stat label="Right BTTS" value={bttsCount} />
        </div>

        <BonusSection bonus={bonus} />

        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Match History</div>

        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading…</div>
        ) : predictions.length === 0 ? (
          <div className="text-center text-white/40 py-20">
            <div className="font-display text-3xl uppercase tracking-tight">No Picks Yet</div>
            <p className="text-sm mt-2">Head to Matches and lock in your first prediction.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {predictions.map((p) => {
              const m = matchesById[p.match_id];
              if (!m) return null;
              const isFinished = m.status === "finished";
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  data-testid={`profile-pred-${p.id}`}
                  className="ring-hover grid grid-cols-[1fr_auto_1fr_auto] items-center gap-4 p-4 rounded-xl bg-surface border border-white/5"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={flagUrl(m.home_code)} className="w-8 h-8 rounded-full ring-1 ring-white/10 object-cover" alt="" />
                    <div className="truncate font-semibold">{m.home_team}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-mono font-bold text-lg">
                      {p.home_score} <span className="text-white/30">:</span> {p.away_score}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                      BTTS: <span className="text-lavender">{p.btts ? "Yes" : "No"}</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                      {fmtUAE(m.kickoff)} {TZ_LABEL}
                    </div>
                    {isFinished && (
                      <div className="font-mono text-[10px] text-white/40 mt-1">
                        Actual {m.home_score}-{m.away_score}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 justify-end min-w-0">
                    <div className="truncate font-semibold text-right">{m.away_team}</div>
                    <img src={flagUrl(m.away_code)} className="w-8 h-8 rounded-full ring-1 ring-white/10 object-cover" alt="" />
                  </div>
                  <div className="flex flex-col items-end gap-1 whitespace-nowrap">
                    {!isFinished ? (
                      <span className="text-xs uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-white/40 border border-white/10">Pending</span>
                    ) : (
                      <>
                        <CritBadge label="Exact" points={p.score_points ?? 0} maxPts={5} />
                        <CritBadge label="Winner" points={p.winner_points ?? 0} maxPts={3} />
                        <CritBadge label="BTTS" points={p.btts_points ?? 0} maxPts={1} />
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function CritBadge({ label, points, maxPts }) {
  const won = points > 0;
  // Won → solid dark purple with white font · Lost → solid dark red with white font
  const toneClass = won
    ? "bg-purple text-white border border-purple shadow-[0_0_12px_rgba(139,32,176,0.55)]"
    : "bg-red-900 text-white border border-red-700/60";
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold ${toneClass}`}>
      {won ? `${label} +${points}` : `${label} 0/${maxPts}`}
    </span>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`tilt-hover rounded-2xl border p-5 ${accent ? "bg-purple/10 border-purple/40" : "bg-surface border-white/5"}`}>
      <div className={`font-mono font-bold text-3xl ${accent ? "text-lavender" : ""}`}>{value}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-2">{label}</div>
    </div>
  );
}

function BonusCard({ b, state }) {
  // state: "won" | "lost" | "pending"
  const borderClass = state === "won"
    ? "border-gold/50 shadow-[0_0_22px_rgba(255,215,0,0.18)]"
    : state === "lost"
      ? "border-red-700 bg-red-950/40 shadow-[0_0_22px_rgba(127,29,29,0.35)]"
      : "border-white/5";
  const badgeClass = state === "won"
    ? "bg-gold/20 text-gold border border-gold/50"
    : state === "lost"
      ? "bg-red-700 text-white border border-red-500"
      : "bg-white/5 text-white/40 border border-white/10";
  const badgeText = state === "won"
    ? `+${b.points} WON`
    : state === "lost"
      ? "LOST"
      : "Pending";

  return (
    <div
      data-testid={`profile-bonus-${b.type}`}
      className={`pop-hover bg-surface border rounded-xl p-4 ${borderClass}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] tracking-widest uppercase text-white/40 truncate">{b.type.replace(/_/g, " ")}</div>
          <div className="font-semibold mt-1 truncate">{b.value}</div>
          {state === "lost" && b.result_value && (
            <div className="text-[10px] tracking-widest uppercase text-white/50 mt-2">
              Actual: <span className="text-red-300 font-bold">{b.result_value}</span>
            </div>
          )}
        </div>
        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${badgeClass}`}>
          {badgeText}
        </span>
      </div>
    </div>
  );
}

function BonusSection({ bonus }) {
  const wonPicks = bonus.filter((b) => (b.points || 0) > 0);
  const lostPicks = bonus.filter((b) => (b.points || 0) === 0 && b.has_result);
  const pendingPicks = bonus.filter((b) => (b.points || 0) === 0 && !b.has_result);
  const wonTotal = wonPicks.reduce((s, b) => s + (b.points || 0), 0);

  if (bonus.length === 0) {
    return (
      <>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Bonus Picks</div>
        <div
          className="bg-surface border border-white/5 rounded-xl p-6 text-center text-white/40 text-sm mb-8"
          data-testid="profile-no-bonus"
        >
          No bonus picks yet. Head to <span className="text-lavender">Bonus</span> to lock them in.
        </div>
      </>
    );
  }

  return (
    <>
      {wonPicks.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold">Bonuses Won</div>
            <div
              data-testid="profile-bonus-won-total"
              className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full bg-gold/20 text-gold border border-gold/50 font-bold"
            >
              +{wonTotal} pts banked
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-8">
            {wonPicks.map((b) => (
              <BonusCard key={b.id} b={b} state="won" />
            ))}
          </div>
        </>
      )}

      {lostPicks.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-400 font-bold">Bonuses Lost</div>
            <div className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full bg-red-700/30 text-red-300 border border-red-500/60 font-bold">
              {lostPicks.length} {lostPicks.length === 1 ? "miss" : "misses"}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-8">
            {lostPicks.map((b) => (
              <BonusCard key={b.id} b={b} state="lost" />
            ))}
          </div>
        </>
      )}

      {pendingPicks.length > 0 && (
        <>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">
            Locked Picks · Awaiting Result
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-8">
            {pendingPicks.map((b) => (
              <BonusCard key={b.id} b={b} state="pending" />
            ))}
          </div>
        </>
      )}
    </>
  );
}
